import React, { useEffect, useState } from 'react';
import './AdminChartPatterns.css';
import { toast } from 'react-toastify';
import { chartPatternAdminApi, ChartPatternTypeAdmin, ChartPatternTypeAdminRequest } from '../../../services/adminApi.ts';

const EMPTY_FORM: ChartPatternTypeAdminRequest = {
  code: '',
  name: '',
  description: '',
  category: 'REVERSAL',
  imageUrl: '',
  scanClause: '',
  columnClause: '',
  columnMapping: '',
  chartinkScreenerId: '',
  isActive: true,
  sortOrder: 0,
};

const AdminChartPatterns: React.FC = () => {
  const [types, setTypes] = useState<ChartPatternTypeAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState<ChartPatternTypeAdminRequest>(EMPTY_FORM);
  const [uploading, setUploading] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const load = () => {
    setLoading(true);
    chartPatternAdminApi.getTypes()
      .then(r => setTypes(r.data?.data ?? r.data))
      .catch(() => toast.error('Failed to load patterns'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setEditId(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (t: ChartPatternTypeAdmin) => {
    setEditId(t.id);
    setForm({
      code: t.code,
      name: t.name,
      description: t.description ?? '',
      category: t.category,
      imageUrl: t.imageUrl ?? '',
      scanClause: t.scanClause ?? '',
      columnClause: t.columnClause ?? '',
      columnMapping: t.columnMapping ?? '',
      chartinkScreenerId: t.chartinkScreenerId ?? '',
      isActive: t.isActive,
      sortOrder: t.sortOrder,
    });
    setShowModal(true);
  };

  const save = async () => {
    try {
      if (editId) {
        await chartPatternAdminApi.updateType(editId, form);
        toast.success('Pattern updated');
      } else {
        await chartPatternAdminApi.createType(form);
        toast.success('Pattern created');
      }
      setShowModal(false);
      load();
    } catch {
      toast.error('Save failed');
    }
  };

  const toggle = async (id: number) => {
    try {
      await chartPatternAdminApi.toggleActive(id);
      load();
    } catch {
      toast.error('Toggle failed');
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      await chartPatternAdminApi.deleteType(deleteId);
      toast.success('Deleted');
      setDeleteId(null);
      load();
    } catch {
      toast.error('Delete failed');
    }
  };

  const uploadImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const r = await chartPatternAdminApi.uploadImage(file);
      const url = r.data?.data ?? r.data;
      setForm(f => ({ ...f, imageUrl: url }));
      toast.success('Image uploaded');
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const fld = (key: keyof ChartPatternTypeAdminRequest, val: any) =>
    setForm(f => ({ ...f, [key]: val }));

  return (
    <div className="acp-page">
      <div className="acp-header">
        <h1>Chart Pattern Types</h1>
        <button className="acp-btn-add" onClick={openAdd}>+ Add Pattern</button>
      </div>

      {loading ? (
        <div className="acp-loading">Loading...</div>
      ) : (
        <div className="acp-table-wrap">
          <table className="acp-table">
            <thead>
              <tr>
                <th>Image</th>
                <th>Code</th>
                <th>Name</th>
                <th>Category</th>
                <th>Sort</th>
                <th>Scan Clause</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {types.map(t => (
                <tr key={t.id}>
                  <td>
                    {t.imageUrl
                      ? <img src={t.imageUrl} alt={t.name} className="acp-thumb" />
                      : <div className="acp-thumb-placeholder">📐</div>}
                  </td>
                  <td><span className="acp-code">{t.code}</span></td>
                  <td>{t.name}</td>
                  <td><span className={`acp-badge ${t.category}`}>{t.category}</span></td>
                  <td>{t.sortOrder}</td>
                  <td>
                    {t.scanClause ? (
                      <div className="acp-clause-cell">
                        <span className="acp-clause-preview">{t.scanClause}</span>
                        <div className="acp-clause-tooltip">{t.scanClause}</div>
                      </div>
                    ) : <span style={{ color: 'var(--admin-text-muted)' }}>—</span>}
                  </td>
                  <td>
                    <span className={`acp-status-dot ${t.isActive ? 'active' : 'inactive'}`} />
                    {' '}{t.isActive ? 'Active' : 'Inactive'}
                  </td>
                  <td>
                    <div className="acp-actions">
                      <button className="acp-btn-icon" onClick={() => openEdit(t)}>Edit</button>
                      <button className="acp-btn-icon" onClick={() => toggle(t.id)}>
                        {t.isActive ? 'Disable' : 'Enable'}
                      </button>
                      <button className="acp-btn-icon danger" onClick={() => setDeleteId(t.id)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="acp-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="acp-modal" onClick={e => e.stopPropagation()}>
            <h2>{editId ? 'Edit Pattern' : 'Add Pattern'}</h2>

            <div className="acp-form-row">
              <div className="acp-form-group">
                <label>Code</label>
                <input
                  value={form.code ?? ''}
                  onChange={e => fld('code', e.target.value)}
                  disabled={!!editId}
                  placeholder="e.g. DOUBLE_BOTTOM"
                />
              </div>
              <div className="acp-form-group">
                <label>Sort Order</label>
                <input
                  type="number"
                  value={form.sortOrder ?? 0}
                  onChange={e => fld('sortOrder', Number(e.target.value))}
                />
              </div>
            </div>

            <div className="acp-form-group">
              <label>Name</label>
              <input value={form.name} onChange={e => fld('name', e.target.value)} placeholder="Double Bottom" />
            </div>

            <div className="acp-form-group">
              <label>Category</label>
              <select value={form.category} onChange={e => fld('category', e.target.value)}>
                <option value="REVERSAL">REVERSAL</option>
                <option value="CONTINUATION">CONTINUATION</option>
              </select>
            </div>

            <div className="acp-form-group">
              <label>Description</label>
              <textarea value={form.description ?? ''} onChange={e => fld('description', e.target.value)} />
            </div>

            <div className="acp-form-group">
              <label>Image URL</label>
              <input value={form.imageUrl ?? ''} onChange={e => fld('imageUrl', e.target.value)} placeholder="Paste URL or upload below" />
              <button className="acp-upload-btn" onClick={() => document.getElementById('acp-upload-input')?.click()} disabled={uploading}>
                {uploading ? 'Uploading...' : 'Upload Image'}
              </button>
              <input id="acp-upload-input" type="file" accept="image/*" style={{ display: 'none' }} onChange={uploadImage} />
              {form.imageUrl && <img src={form.imageUrl} alt="preview" className="acp-img-preview" />}
            </div>

            <div className="acp-form-group">
              <label>Scan Clause</label>
              <textarea value={form.scanClause ?? ''} onChange={e => fld('scanClause', e.target.value)} placeholder="ChartInk scan clause (optional)" />
            </div>

            <div className="acp-form-group">
              <label>Column Clause</label>
              <textarea value={form.columnClause ?? ''} onChange={e => fld('columnClause', e.target.value)} placeholder="ChartInk column clause (optional)" />
            </div>

            <div className="acp-form-group">
              <label>Column Mapping <span style={{ fontWeight: 400, fontSize: '0.75rem', color: 'var(--admin-text-muted)' }}>(JSON — alias → db field)</span></label>
              <textarea value={form.columnMapping ?? ''} onChange={e => fld('columnMapping', e.target.value)} placeholder='e.g. {"marketcapname":"marketCapName","marketcap":"marketCap","sector":"sector","industry":"industry"}' />
            </div>

            <div className="acp-form-group">
              <label>ChartInk Screener ID</label>
              <input value={form.chartinkScreenerId ?? ''} onChange={e => fld('chartinkScreenerId', e.target.value)} placeholder="e.g. flag-860" />
            </div>

            <div className="acp-toggle-row">
              <input type="checkbox" id="acp-active" checked={!!form.isActive} onChange={e => fld('isActive', e.target.checked)} />
              <label htmlFor="acp-active">Active</label>
            </div>

            <div className="acp-modal-actions">
              <button className="acp-btn-cancel" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="acp-btn-save" onClick={save}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteId && (
        <div className="acp-modal-overlay" onClick={() => setDeleteId(null)}>
          <div className="acp-modal" onClick={e => e.stopPropagation()}>
            <h2>Confirm Delete</h2>
            <p>Are you sure you want to delete this pattern? This will also remove all scan results linked to it.</p>
            <div className="acp-modal-actions">
              <button className="acp-btn-cancel" onClick={() => setDeleteId(null)}>Cancel</button>
              <button className="acp-btn-delete-confirm" onClick={confirmDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminChartPatterns;
